import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Buildings, CaretDown, Check, Warning, SpinnerGap } from "@phosphor-icons/react";
import { AuthContext } from "../contexts/AuthContext";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
import { getLayoutBootstrap } from "../lib/layoutBootstrap"; // B1099
import { useLang } from "../contexts/LanguageContext";

export default function OrgSwitcher() {
  const { tr } = useLang();
  const { user, refreshUser } = useContext(AuthContext);
  const [orgs, setOrgs] = useState([]);
  const [canSwitch, setCanSwitch] = useState(false);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchingTo, setSwitchingTo] = useState(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    // B1099 (Eric 2026-09-07) — La liste des organisations vient de l'appel
    // agrégé du gabarit (elle ne change qu'au rechargement complet de page,
    // la bascule d'org faisant un `location.replace`).
    getLayoutBootstrap()
      .then((boot) => {
        if (!boot?.organizations) return;
        if (cancelled) return;
        setOrgs(boot.organizations.organizations || []);
        setCanSwitch(!!boot.organizations.can_switch);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const handleSwitch = async (orgId) => {
    if (orgId === user?.org_id) {
      toast.info(tr("Vous êtes déjà sur cette organisation", "You are already on this organisation"), { duration: 2000 });
      setOpen(false);
      return;
    }
    setSwitching(true);
    setSwitchingTo(orgId);
    try {
      await axios.post(
        `${API}/api/organizations/switch`,
        { org_id: orgId },
        { withCredentials: true }
      );
      // CRITICAL: Force a true hard reload IMMEDIATELY (no setTimeout) to avoid any
      // race condition where stale data could load between the switch and the reload.
      // We append a unique cache-buster query param so the browser cannot serve a
      // cached HTML/JS bundle, AND we use window.location.replace which is the
      // most reliable cross-browser way to trigger a full page navigation.
      const cacheBuster = Date.now();
      window.location.replace(`/dashboard?org=${encodeURIComponent(orgId)}&_t=${cacheBuster}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" && detail
        ? detail
        : e?.message === "Network Error"
          ? tr("Impossible de joindre le serveur. Vérifiez votre connexion.", "Cannot reach the server. Check your connection.")
          : tr("Impossible de changer d'organisation. Réessayez.", "Could not switch organisation. Try again.");
      toast.error(msg, { duration: 4500 });
      setSwitching(false);
      setSwitchingTo(null);
    }
  };

  if (!user || orgs.length === 0) return null;

  // CRITICAL: never fall back to orgs[0] when the active org is not found in
  // the dropdown list — that fallback caused the inverted-data bug where the
  // header showed e.g. "9439" while the dashboard data was actually from
  // Knoria (because every other page reads user.org_id directly from /auth/me).
  // If user.org_id is somehow missing from the list, we display the user's
  // own org_id literally so the mismatch is visible to the user instead of
  // being silently masked.
  const currentOrg = orgs.find((o) => o.id === user.org_id);
  const headerLabel = currentOrg?.name
    || (user.org_id ? `(${user.org_id})` : "—");

  // If user can't switch and has only one org, show a static badge (no dropdown)
  if (!canSwitch && orgs.length <= 1) {
    return (
      <div
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/60 text-xs"
        data-testid="org-switcher-static"
      >
        <Buildings size={14} className="text-muted-foreground" />
        <span className="font-medium truncate max-w-[160px]">{headerLabel}</span>
      </div>
    );
  }

  return (
    <div className="relative" data-testid="org-switcher">
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/60 hover:bg-secondary border border-transparent hover:border-border transition-all text-xs disabled:opacity-50"
        data-testid="org-switcher-btn"
      >
        <Buildings size={14} className="text-[#002FA7]" />
        <span className="font-semibold truncate max-w-[140px] hidden sm:inline">
          {headerLabel}
        </span>
        <CaretDown size={12} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="org-switcher-backdrop"
          />
          <div
            className="absolute right-0 top-full mt-2 w-72 bg-card border border-border rounded-lg shadow-2xl z-50 overflow-hidden"
            data-testid="org-switcher-dropdown"
          >
            <div className="px-3 py-2.5 border-b border-border bg-secondary/40">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {tr("Organisation active", "Active organisation")}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {orgs.map((org) => {
                const isActive = org.id === user.org_id;
                const isSwitchingThis = switchingTo === org.id;
                return (
                  <button
                    key={org.id}
                    onClick={() => handleSwitch(org.id)}
                    disabled={switching}
                    className={`w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-secondary transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                      isActive ? "bg-[#002FA7]/5" : ""
                    } ${isSwitchingThis ? "bg-[#002FA7]/10" : ""}`}
                    data-testid={`org-switcher-item-${org.id}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                        isActive ? "bg-[#002FA7] text-white" : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {isSwitchingThis ? (
                        <SpinnerGap size={15} className="animate-spin" />
                      ) : (
                        <Buildings size={15} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                        {org.name}
                        {org.is_platform && (
                          <span className="text-[9px] bg-[#38BDF8]/15 text-[#38BDF8] font-bold px-1.5 py-0.5 rounded">
                            PLATEFORME
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {isSwitchingThis
                          ? tr("Bascule en cours…", "Switching…")
                          : `${org.plan || "—"} · ${org.data_region || "Canada"}`}
                      </div>
                    </div>
                    {isActive && !isSwitchingThis && <Check size={16} className="text-[#002FA7] flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-border bg-amber-50/50 dark:bg-amber-500/5">
              <div className="flex items-start gap-2">
                <Warning size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-amber-800 dark:text-amber-300 leading-snug">
                  {tr("Isolation stricte des données : chaque organisation a sa propre base de connaissances, ses vidéos, ses quiz et ses utilisateurs.", "Strict data isolation: every organisation has its own knowledge base, videos, quizzes and users.")}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
